import React, {ComponentProps, useCallback, useRef} from 'react';
import clsx from 'clsx';
import {FixedSizeList, VariableSizeList} from 'react-window';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {useArrowFocusTrap} from '../../hooks';
import {Action} from '../../store/actions';
import {withArrowFocusTrap} from '../../withArrowFocusTrap';
import {
	ARROW_FOCUS_TRAP_ITEMS_CLASS_NAME,
	arrowFocusTrapItemsClasses,
	ArrowFocusTrapItemsClasses,
	getArrowFocusTrapItemsUtilityClass,
} from './arrowFocusTrapItemsClasses';

type Ref = React.Ref<HTMLDivElement>;

type ArrowFocusTrapItemsUtiltiyClassProps = WithClasses<
	ArrowFocusTrapItemsClasses,
	{
		trapActive?: boolean;
	}
>;

const useUtilityClasses = (
	ownerState: ArrowFocusTrapItemsUtiltiyClassProps,
) => {
	const {classes, trapActive} = ownerState;
	const slots = {
		root: ['root', trapActive && 'trapActive'],
	};
	return composeClasses(slots, getArrowFocusTrapItemsUtilityClass, classes);
};

const ArrowFocusTrapItemsRoot = styled('div', {
	name: ARROW_FOCUS_TRAP_ITEMS_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
}));

const FOCUSABLE_ELEMENTS =
	'a:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

function focusPreviousElement() {
	if (!document.activeElement) return;

	const focussable = Array.prototype.filter.call(
		document.querySelectorAll(FOCUSABLE_ELEMENTS),
		(element: HTMLElement) =>
			element.offsetWidth > 0 ||
			element.offsetHeight > 0 ||
			element === document.activeElement,
	);

	const index = focussable.indexOf(document.activeElement);
	if (index < 0) return;

	const nextElement = focussable[index - 1] || focussable[0];
	nextElement?.focus({preventScroll: true});
}

export interface ArrowFocusTrapItemsProps<
	E extends React.ElementType = 'div',
> extends React.DetailedHTMLProps<React.HTMLAttributes<E>, E>,
		WithClasses<
			ArrowFocusTrapItemsClasses,
			React.PropsWithChildren<{
				align?: 'auto' | 'smart' | 'center' | 'end' | 'start';
				disableScrollToOnFocusCapture?: boolean;
				returnToIndexOnFocus?: number;
				//ref?: React.LegacyRef<HTMLDivElement>;
				virtualListRef?: React.RefObject<
					FixedSizeList | VariableSizeList | null
				>;
			}>
		> {
	component?: E;
	componentProps?: ComponentProps<E>;
	ref?: React.ForwardedRef<any>;
}

const ArrowFocusTrapItemsInner = <E extends React.ElementType = 'div'>(
	{
		classes: _classes,
		className,
		children: childrenProp,
		component = 'div' as E,
		componentProps,
		align = 'smart',
		disableScrollToOnFocusCapture,
		returnToIndexOnFocus,
		virtualListRef,
		...props
	}: ArrowFocusTrapItemsProps<E>,
	ref: Ref,
) => {
	const {
		activeDescendant,
		clickKeysDisabled,
		disableNextScrollUpdate,
		sendNextFocusToPreviousElement,
		disabled,
		userDisabled,
		dispatch,
		activeIndex,
		keysDisabled,
		justClickedItem,
		returnToIndexOnFocus: _returnToIndexOnFocus,
		virtualListRef: contextVirtualListRef,
	} = useArrowFocusTrap();

	// TODO: c'mon... gross
	// really we need to get rid of the items component (as well as all the others)
	// and converting what is left over to another hook (like items (item, action))
	const __returnToIndexOnFocus =
		_returnToIndexOnFocus > -1 ? _returnToIndexOnFocus : returnToIndexOnFocus;

	const stateRef = useRef({
		activeIndex,
		clickKeysDisabled,
		disabled,
		userDisabled,
		keysDisabled,
	});
	stateRef.current = {
		activeIndex,
		clickKeysDisabled,
		disabled,
		userDisabled,
		keysDisabled,
	};

	const rootRef = useSharedRef<HTMLDivElement | null>(null, [ref]);

	const blurTimeout = useRef<NodeJS.Timeout>();
	const hasFocus = useRef(false);

	const classes = useUtilityClasses({
		classes: _classes,
		trapActive: !disabled,
	});

	useWhenChangedEffect(
		activeIndex,
		useCallback(
			(activeIndex) => {
				const listRef = virtualListRef || contextVirtualListRef;
				if (
					justClickedItem.current ||
					!listRef?.current ||
					(listRef.current.state as any).isScrolling ||
					disableScrollToOnFocusCapture
				)
					return;

				listRef.current.scrollToItem(activeIndex, align);
			},
			[disabled, disableScrollToOnFocusCapture, align, userDisabled],
		),
		{
			executeAtMount: true,
		},
	);

	const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
		// blur will occur when moving to anither element even if that
		// element is a descendant of ours (since that is how bubbled
		// events work)
		if (blurTimeout.current) clearTimeout(blurTimeout.current);
		// if (focusTimeout.current) clearTimeout(focusTimeout.current);
		blurTimeout.current = setTimeout(() => {
			dispatch({type: Action.Disable});
			hasFocus.current = false;
		}, 16);
	}, []);

	// const focusTimeout = useRef<NodeJS.Timeout>();
	const handleFocus = useCallback(
		(event: React.FocusEvent<HTMLDivElement>) => {
			if (blurTimeout.current) clearTimeout(blurTimeout.current);

			// if (focusTimeout.current) clearTimeout(focusTimeout.current);

			const sendFocus = sendNextFocusToPreviousElement.current;
			sendNextFocusToPreviousElement.current = false;

			if (sendFocus) {
				focusPreviousElement();
				return;
			}

			if (hasFocus.current) {
				dispatch({type: Action.Enable});
				return;
			}

			hasFocus.current = true;

			//focusTimeout.current = setTimeout(() => {
			if (
				!justClickedItem.current &&
				__returnToIndexOnFocus != null &&
				__returnToIndexOnFocus > -1
			) {
				dispatch({type: Action.SetActiveItem, index: __returnToIndexOnFocus});
			}

			!stateRef.current.userDisabled && dispatch({type: Action.Enable});

			disableNextScrollUpdate.current = true;

			const listRef = virtualListRef || contextVirtualListRef;

			if (disableScrollToOnFocusCapture || !listRef?.current) return;

			const {activeIndex} = stateRef.current;
			if (
				!(listRef.current.state as any).isScrolling &&
				!stateRef.current.disabled &&
				event.target === rootRef.current
			) {
				listRef.current.scrollToItem(activeIndex);
			}
			//}, 16);
		},
		[__returnToIndexOnFocus, disableScrollToOnFocusCapture],
	);

	return (
		<ArrowFocusTrapItemsRoot
			{...(componentProps as any)}
			{...(props as any)}
			as={component}
			aria-activedescendant={
				disabled || userDisabled ? undefined : activeDescendant
			}
			ref={rootRef}
			className={clsx(classes.root, className)}
			onBlur={handleBlur}
			onFocus={handleFocus}
			tabIndex={0}
		>
			{childrenProp}
		</ArrowFocusTrapItemsRoot>
	);
};

export const ArrowFocusTrapItemsPrimitive = React.forwardRef<
	HTMLDivElement,
	ArrowFocusTrapItemsProps
>(ArrowFocusTrapItemsInner) as <E extends React.ElementType = 'div'>(
	props: ArrowFocusTrapItemsProps<E>,
) => JSX.Element;

export const ArrowFocusTrapItems = withArrowFocusTrap(
	ArrowFocusTrapItemsPrimitive,
);

export default ArrowFocusTrapItems;
